import { createSelector } from '@reduxjs/toolkit';
import { edgeApi } from '../../services/edgeApi';
import { createFilter } from '../../utils/objectUtils';
import { MapState } from './mapSlice';
import {
  Node,
  Interconnect,
  Point,
  Edge,
  SegmentFlowDirection,
  Region,
} from '@/types';
import { binaryToPointDict, pointDictToBinary } from '../../utils/dataUtils';
import { selectPriceComponentsResult } from '../../plotter/plotterSelectors';
import { RootState } from '@/store';

export const selectMapPointsResult = edgeApi.endpoints.getMapPoints.select();
export const selectMapNodesResult =
  edgeApi.endpoints.getMapNodes.select(undefined);
export const selectMapEdgesResult = edgeApi.endpoints.getMapEdges.select();
export const selectTspsResult = edgeApi.endpoints.getTSPs.select(undefined);
export const selectInterconnectsResult =
  edgeApi.endpoints.getInterconnects.select(undefined);
export const selectRegionsResult =
  edgeApi.endpoints.getRegions.select(undefined);

export const selectMapState = (state: { map: MapState }) => state.map ?? {};

export const selectIsMapLoading = createSelector(
  selectMapPointsResult,
  selectMapNodesResult,
  selectMapEdgesResult,
  selectInterconnectsResult,
  (pointsResult, nodesResult, edgesResult, interconnectsResult) => {
    return (
      pointsResult.isLoading ||
      nodesResult.isLoading ||
      edgesResult.isLoading ||
      interconnectsResult.isLoading
    );
  },
);

/**
 * Select nodes response from the API. The response can be cached. If the API has not
 * yet resolved, it will return the locally cached nodes from the last successful request.
 */
export const selectApiNodes = createSelector(
  selectMapNodesResult,
  (mapResult) => {
    if (mapResult?.data && mapResult?.fulfilledTimeStamp) {
      if (
        localStorage.getItem('noCache') !== 'true' &&
        mapResult.fulfilledTimeStamp >
          Number(localStorage.getItem('apiNodesTimeStamp'))
      ) {
        console.info('caching nodes to local storage');
        try {
          localStorage.setItem('apiNodes', JSON.stringify(mapResult.data));
          localStorage.setItem(
            'apiNodesTimeStamp',
            mapResult.fulfilledTimeStamp.toString(),
          );
        } catch (e) {
          console.info('caching nodes failed');
        }
      }
      return mapResult.data;
    } else {
      const cachedNodes: Record<string, Node> = JSON.parse(
        localStorage.getItem('apiNodes') ?? '{}',
      );
      return cachedNodes;
    }
  },
);

/**
 * Select points response from the API. The response can be cached. If the API has not
 * yet resolved, it will return the locally cached points from the last successful request.
 */
export const selectApiPoints = createSelector(
  selectMapPointsResult,
  (mapResult) => {
    if (mapResult?.data && mapResult?.fulfilledTimeStamp) {
      if (
        localStorage.getItem('noCache') !== 'true' &&
        mapResult.fulfilledTimeStamp >
          Number(localStorage.getItem('apiPointsTimeStamp'))
      ) {
        console.info('caching points to local storage');
        const mappedPoints = Object.keys(mapResult.data).reduce<
          Record<string, Point>
        >((acc, key) => {
          const point = mapResult.data?.[key];
          if (point && point.node !== null && point.node !== undefined) {
            acc[key] = point;
          }
          return acc;
        }, {});
        try {
          localStorage.setItem('apiPoints', pointDictToBinary(mappedPoints));
          localStorage.setItem(
            'apiPointsTimeStamp',
            mapResult.fulfilledTimeStamp.toString(),
          );
          localStorage.setItem('apiPointsCacheVersion', '1');
        } catch (e) {
          console.info('caching points failed');
        }
      }
      return mapResult.data;
    } else {
      const cacheVersion = Number(
        localStorage.getItem('apiPointsCacheVersion') ?? '0',
      );
      const cachedPoints: Record<string, Point> =
        cacheVersion === 1
          ? binaryToPointDict(localStorage.getItem('apiPoints') ?? '')
          : JSON.parse(localStorage.getItem('apiPoints') ?? '{}');
      return cachedPoints;
    }
  },
);

/**
 * Select edges response from the API. The response can be cached. If the API has not
 * yet resolved, it will return the locally cached edges from the last successful request.
 */
export const selectApiEdges = createSelector(
  selectMapEdgesResult,
  (mapResult) => {
    if (mapResult?.data && mapResult?.fulfilledTimeStamp) {
      if (
        localStorage.getItem('noCache') !== 'true' &&
        mapResult.fulfilledTimeStamp >
          Number(localStorage.getItem('apiEdgesTimeStamp'))
      ) {
        console.info('caching edges to local storage');
        try {
          localStorage.setItem('apiEdges', JSON.stringify(mapResult.data));
          localStorage.setItem(
            'apiEdgesTimeStamp',
            mapResult.fulfilledTimeStamp.toString(),
          );
        } catch (e) {
          console.info('caching edges failed');
        }
      }
      return mapResult.data;
    } else {
      const cachedEdges: Record<string, Edge> = JSON.parse(
        localStorage.getItem('apiEdges') ?? '{}',
      );
      return cachedEdges;
    }
  },
);

/**
 * Select interconnects response from the API. The response can be cached.
 */
export const selectInterconnects = createSelector(
  selectInterconnectsResult,
  (interconnectsResult) => interconnectsResult?.data ?? {},
);

/**
 * Select a dictionary of interconnects indexed by their start and end points.
 */
export const selectInterconnectsAtPoints = createSelector(
  selectInterconnects,
  (interconnects) =>
    Object.values(interconnects).reduce<Record<string, Interconnect[]>>(
      (acc, interconnect) => {
        if (!acc[interconnect.start_point]) {
          acc[interconnect.start_point] = [];
        }
        if (!acc[interconnect.end_point]) {
          acc[interconnect.end_point] = [];
        }
        acc[interconnect.start_point].push(interconnect);
        acc[interconnect.end_point].push(interconnect);
        return acc;
      },
      {},
    ),
);

/**
 * Select tsps response from the API. The response can be cached.
 */
export const selectTsps = createSelector(
  selectTspsResult,
  (tspsResult) => tspsResult?.data ?? {},
);

/**
 * Select an array of tsps sorted by their name.
 */
export const selectSortedTsps = createSelector(selectTsps, (tsps) =>
  Object.values(tsps).sort((a, b) => a.name.localeCompare(b.name)),
);

export const selectPriceComponents = createSelector(
  selectPriceComponentsResult,
  (priceComponentsResult) => priceComponentsResult?.data ?? {},
);

/**
 * Select an array of tsp options for use in MultiSelectSearch.
 */
export const selectTspOptions = createSelector(selectSortedTsps, (tsps) =>
  tsps.map((tsp) => ({
    value: tsp.id,
    label: tsp.name,
    searchValues: tsp.aliases ?? [],
  })),
);

/**
 * Select unfiltered points from the API and the new points added by the user.
 */
export const selectAllNodes = createSelector(
  selectApiNodes,
  (apiNodes) => apiNodes ?? {},
);

/**
 * Select unfiltered points from the API.
 */
export const selectAllPoints = createSelector(
  selectApiPoints,
  (apiPoints) => apiPoints,
);

export const selectFilterRegions = createSelector(
  selectRegionsResult,
  (state: RootState) =>
    state.filters.regionAttributes?.id as number[] | undefined,
  (regionsResult, regionIds) => {
    const regions = regionsResult?.data ?? [];
    if (!regionIds || regionIds.length === 0) {
      return [];
    }
    return regions.filter((region) => regionIds.includes(region.id));
  },
);

const checkPointInRegions = (point: Point, regions: Region[]) => {
  if (regions.length <= 0) return true;
  return regions.some(
    (region) =>
      region.tsps.some((tsp) => tsp.id === point.tsp_id) ||
      region.zones.some(
        (zone) =>
          zone.id === point.receipt_zone || zone.id === point.delivery_zone,
      ),
  );
};

const checkEdgeInRegions = (edge: Edge, regions: Region[]) => {
  if (regions.length <= 0) return true;
  return regions.some(
    (region) =>
      region.tsps.some((tsp) => tsp.id === edge.tsp_id) ||
      region.zones.some((zone) => zone.id === edge.zone_id),
  );
};

/**
 * Select filtered point ids.
 */
export const selectAttributeFilteredPointIds = createSelector(
  [
    selectAllPoints,
    (state) => state.filters.pointAttributes as Record<string, any>,
    selectFilterRegions,
  ],
  (apiPoints, filters, regionFilters) => {
    let points = Object.values(apiPoints);
    if (Object.keys(filters || {}).length > 0) {
      points = points.filter(createFilter(filters));
    }
    points = points.filter((point) =>
      checkPointInRegions(point, regionFilters),
    );

    return points.map((point) => String(point.id));
  },
);

/**
 * Select points on filtered edges.
 */
export const selectFilteredEdgePoints = createSelector(
  [
    selectApiPoints,
    (state) => state.filters.pointAttributes?.tsp_id as number[],
    selectFilterRegions,
  ],
  (apiPoints, tsp_ids, regionFilters) => {
    if (!tsp_ids || tsp_ids.length === 0) {
      return apiPoints;
    }

    let points = Object.values(apiPoints).filter((point) =>
      tsp_ids.includes(point.tsp_id),
    );
    points = points.filter((point) =>
      checkPointInRegions(point, regionFilters),
    );

    return points.reduce<Record<string, Point>>((obj, point) => {
      obj[point.id] = point;
      return obj;
    }, {});
  },
);

/**
 * Select nodes on filtered edges and any new nodes added during the current session.
 */
export const selectFilteredEdgeNodes = createSelector(
  selectApiNodes,
  selectFilteredEdgePoints,
  selectFilterRegions,
  (apiNodes, filteredPoints, regionFilters) => {
    const filteredNodes: Record<string, Node> = {};
    Object.keys(filteredPoints).map((pointId) => {
      const nodeId = filteredPoints[pointId].node?.toString();
      if (nodeId && checkPointInRegions(filteredPoints[pointId], regionFilters))
        filteredNodes[nodeId] = apiNodes[nodeId];
    });
    return { ...filteredNodes };
  },
);

/**
 * Select points under conditional filters.
 */
export const selectCustomFilteredPointIds = createSelector(
  [
    selectApiPoints,
    selectApiNodes,
    selectAttributeFilteredPointIds,
    selectInterconnects,
    (state) => state.filters.custom as Record<string, any>,
  ],
  (apiPoints, apiNodes, filteredPointIds, interconnects, customFilters) => {
    const interconnectPoints = Object.values(interconnects).flatMap(
      (interconnect) => [
        String(interconnect.start_point),
        String(interconnect.end_point),
      ],
    );

    if (customFilters?.onlyKnownInterconnects) {
      return interconnectPoints;
    }

    if (customFilters?.onlyPotentialInterconnects) {
      const apiPointArray = filteredPointIds.map(
        (pointId) => apiPoints[pointId],
      );
      const tspFercCids = apiPointArray
        .map((point) => point.tsp_ferc_cid)
        .filter(Boolean);
      const uniqueFercCids = [...new Set(tspFercCids)];
      const potentialInterconnect = apiPointArray
        .filter((point) => uniqueFercCids.includes(point.up_dn_ferc_cid))
        .filter((point) => point.up_dn_ferc_cid != point.tsp_ferc_cid)
        .map((point) => String(point.id))
        .filter((id) => !interconnectPoints.includes(id));

      return potentialInterconnect;
    }

    if (customFilters?.missingZone) {
      return Object.keys(apiPoints).filter(
        (pointId) => !apiPoints[pointId].receipt_zone,
      );
    }

    if (customFilters?.noEdges) {
      return Object.keys(apiNodes)
        .map((nodeId) => apiNodes[nodeId])
        .filter(
          (node) =>
            node.starts_edges.length == 0 && node.ends_edges.length == 0,
        )
        .flatMap((node) => String(node.points));
    }

    return Object.keys(apiPoints);
  },
);

/**
 * Select points that are filtered by both attribute and custom filters.
 */
export const selectFilteredPoints = createSelector(
  selectApiPoints,
  selectAttributeFilteredPointIds,
  selectCustomFilteredPointIds,
  (apiPoints, attributePointIds, customPointIds) => {
    const intersection = attributePointIds.filter((attributePointId) =>
      customPointIds.includes(attributePointId),
    );
    return intersection.reduce<Record<string, Point>>((obj, pointId) => {
      obj[pointId] = apiPoints[pointId];
      return obj;
    }, {});
  },
);

/**
 * Select nodes that are filtered by both attribute and custom filters.
 */
export const selectFilteredNodes = createSelector(
  [selectApiNodes, selectFilteredPoints, (state) => state.filters],
  (apiNodes, filteredPoints, filters) => {
    if (Object.keys(filters).length === 0) {
      return apiNodes;
    }

    const filteredNodes: Record<string, Node> = {};
    Object.values(filteredPoints).map((point) => {
      const nodeId = point.node;
      if (nodeId) filteredNodes[nodeId] = apiNodes[nodeId];
    });
    return filteredNodes;
  },
);

export const selectFilteredInterconnects = createSelector(
  [selectInterconnects, selectFilteredPoints],
  (interconnects, filteredPoints) => {
    return Object.values(interconnects).filter(
      (interconnect) =>
        filteredPoints[String(interconnect.start_point)] ||
        filteredPoints[String(interconnect.end_point)],
    );
  },
);

/**
 * Select all edges from the api.
 */
export const selectAllEdges = createSelector(
  selectApiEdges,
  (apiEdges) => apiEdges,
);

export const selectEdgeLookup = createSelector(selectAllEdges, (edges) => {
  const edgeLookup: Record<string, [Edge, SegmentFlowDirection]> = {};
  Object.values(edges).forEach((edge) => {
    const fhKey = `${edge.start_node}-${edge.end_node}`;
    const bhKey = `${edge.end_node}-${edge.start_node}`;
    edgeLookup[fhKey] = [edge, 'FORWARDHAUL'];
    edgeLookup[bhKey] = [edge, 'BACKHAUL'];
  });
  return edgeLookup;
});

/**
 * Select edges that are filtered by both attribute.
 */
export const selectFilteredEdges = createSelector(
  [
    selectApiEdges,
    (state) => state.filters.pointAttributes?.tsp_id as number[],
    selectFilterRegions,
  ],
  (apiEdges, filteredTsps, regionFilters) => {
    const filteredEdges = Object.keys(apiEdges).reduce<Record<string, Edge>>(
      (acc, edgeId) => {
        const edge = apiEdges[edgeId];
        if (
          (!filteredTsps ||
            filteredTsps.length === 0 ||
            filteredTsps.includes(edge.tsp_id)) &&
          checkEdgeInRegions(edge, regionFilters)
        ) {
          acc[edgeId] = edge;
        }
        return acc;
      },
      {},
    );

    return filteredEdges;
  },
);

export const selectFilteredEdgesByZone = createSelector(
  selectFilteredEdges,
  (edges) => {
    const edgesByZone = Object.values(edges).reduce<
      Record<string, Record<string, Edge[]>>
    >((acc, edge) => {
      if (!acc[edge.tsp_id]) {
        acc[edge.tsp_id] = {};
      }
      if (!acc[edge.tsp_id]![edge.zone_id]) {
        acc[edge.tsp_id]![edge.zone_id] = [];
      }
      acc[edge.tsp_id]![edge.zone_id]!.push(edge);
      return acc;
    }, {});
    return edgesByZone;
  },
);

/**
 * Select ids of filtered tsps
 */
export const selectFilteredTsps = createSelector(
  [selectTsps, (state) => state.filters.pointAttributes?.tsp_id as number[]],
  (_tsps, tspIds) => tspIds ?? [],
);

/**
 * Select temporary node created during the current session.
 */
export const selectEditorNode = createSelector(
  selectMapState,
  (state) => state.editor.node,
);

/**
 * Select the start node of the edge being created.
 */
export const selectEditorEdgeStartNode = createSelector(
  selectMapState,
  selectAllNodes,
  (state, nodes) => nodes[String(state.editor.edgeStartNode)],
);

/**
 * Select the end node of the edge being created.
 */
export const selectEditorEdgeEndNode = createSelector(
  selectMapState,
  selectAllNodes,
  (state, nodes) => nodes[String(state.editor.edgeEndNode)],
);

/**
 * Select the editor mode
 */
export const selectEditorMode = createSelector(
  selectMapState,
  /**
   *
   * @param {import('./mapSlice').MapState} state
   */
  (state) => state.editor.mode,
);

export const selectEditorMoveNode = createSelector(
  selectMapState,
  (state) => state.editor.moveNode,
);

export const selectPaths = createSelector(
  selectMapState,
  (state) => state.paths,
);

export const selectHighlightOptions = createSelector(
  selectMapState,
  (state) => state.highlight,
);

export const selectHighlightedEdges = createSelector(
  selectFilteredEdgesByZone,
  selectHighlightOptions,
  (filteredEdges, highlightOptions) => {
    const edges = Object.entries(filteredEdges).flatMap(([tspId, zonesMap]) => {
      if (highlightOptions.tsps.has(Number(tspId))) {
        return Object.values(zonesMap).flat();
      }
      return Object.entries(zonesMap).flatMap(([zoneId, edges]) => {
        if (highlightOptions.zones.has(Number(zoneId))) {
          return edges;
        }
        return [];
      });
    });
    return edges;
  },
);
